import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Tooltip } from 'antd';
import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { useDispatch, useSelector } from 'react-redux';
import Icons from '../../../configs/icon.config';
import Translate from '../Translate';
import { commonState, updateNoticeStatus } from '../../../app/base/commonSlice';

import './styles.scss';

interface NoticeP {
  contentEn?: string;
  contentBn?: string;
}

const noticeCharacterLength = 200;
const noticeCharacterLengthBn = 300;

function Notice({ contentEn, contentBn }: NoticeP) {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [ellipsis, setEllipsis] = useState(true);
  const [showTooltip, setShowTooltip] = useState(true);
  const { language } = useSelector(commonState);
  const isEnglish = useMemo(() => language === 'en', [language]);

  const [isHideAnimation, setIsHideAnimation] = useState(false);
  const [noticeFloatingButtonPosition, setNoticeFloatingButtonPosition] =
    useState({
      positionX: 0,
      positionY: 0,
    });
  const noticeIconRef = useRef<any>();
  const { isNoticeOpen, isAdmin, floatButtonCount } = useSelector(commonState);

  const dispatch = useDispatch();

  const handleNoticClose = () => {
    setIsHideAnimation(true);
    setTimeout(() => {
      dispatch(updateNoticeStatus(false));
    }, 300);
    setIsHideAnimation(false);
    setTimeout(() => {
      setShowTooltip(true);
    }, 1250);
  };

  const handleNoticeShow = () => {
    setShowTooltip(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (window.scrollY > 0) {
      // if the scroll position is > 0, then delay 600ms to reach the scroll position at the top
      setTimeout(() => {
        dispatch(updateNoticeStatus(true));
      }, 600);
    } else {
      dispatch(updateNoticeStatus(true));
    }
  };

  useEffect(() => {
    if (noticeIconRef.current) {
      const noticeButtonPositon = noticeIconRef.current.getBoundingClientRect();

      if (isNoticeOpen) {
        setNoticeFloatingButtonPosition({
          positionX:
            window.innerWidth - (Number(noticeButtonPositon?.left) + 160), // button default position in x axis is 60
          positionY:
            window.innerHeight - (Number(noticeButtonPositon?.top) + 90), // button default position in y axis is 190
        });
      } else {
        setNoticeFloatingButtonPosition({
          positionX: 0,
          positionY: 0,
        });
      }
    }
  }, [isNoticeOpen]);

  const noticeFloatingButton = () => {
    return (
      <button
        style={{
          transform: `translate(-${noticeFloatingButtonPosition.positionX}px,
            -${noticeFloatingButtonPosition.positionY}px)`,
        }}
        className={classNames(
          'notice-floating-icon',
          {
            'show-floating-notice-button': !isNoticeOpen,
            'hide-floating-notice-button': isNoticeOpen,
          },
          `${floatButtonCount === 3 ? 'right-[115px]' : 'right-[69px]'}`
        )}
        onClick={handleNoticeShow}
      >
        {Icons.megaPhone}
      </button>
    );
  };

  const truncate = (data: string) => {
    const dataLength = data.length;
    if (dataLength <= noticeCharacterLength && isEnglish) {
      return data;
    }

    if (dataLength <= noticeCharacterLengthBn && !isEnglish) {
      return data;
    }

    if (ellipsis) {
      const tempStr = data.substring(
        0,
        isEnglish ? noticeCharacterLength : noticeCharacterLengthBn
      );
      const convertStrToArr = tempStr.split(' ');
      // remove last two element
      convertStrToArr.length -= 2;

      return `${convertStrToArr.join(' ')}...`;
    }

    return data;
  };

  const seeMore = () => {
    return (
      <button
        onClick={() => {
          setEllipsis((prev) => !prev);
        }}
        className="float-right underline flex items-center gap-3 text-[#D10D74] dark:text-[#EB3E8E]"
      >
        <Translate
          en={ellipsis ? 'Read more' : 'Read less'}
          bn={ellipsis ? 'আরো পড়ুন' : 'সংক্ষিপ্ত রুপে দেখুন'}
        />
      </button>
    );
  };

  return (
    <div>
      {!isAdmin && (
        <div
          className={classNames('notice-area', {
            'notice-in-active': !isNoticeOpen,
            inactivating: isHideAnimation,
            'notice-active': isNoticeOpen,
          })}
          key={isNoticeOpen ? 1 : 0}
          ref={isMobile ? noticeIconRef : null}
        >
          <Alert
            className="shadow-2xl border-0"
            message={
              <div>
                {/* notice button will be shown only in the mobile device  */}
                <div
                  className="text-[26px] sm:block md:hidden ml-3"
                  // ref={noticeIconRef}
                >
                  {Icons.megaPhone}
                </div>
                <div className="text-[18px] notice-box p-3 flex gap-10 items-center">
                  {/* notice button will be shown in the learge device. will be hidden in the mobile device  */}
                  <div
                    className="text-[60px] hidden md:block"
                    ref={noticeIconRef}
                  >
                    {Icons.megaPhone}
                  </div>
                  {isMobile ? (
                    // eslint-disable-next-line react/jsx-no-useless-fragment
                    <div className="relative">
                      <Translate
                        en={`${truncate(contentEn || '')}`}
                        bn={`${truncate(contentBn || '')}`}
                      />
                      {/* show more button for english if content length is > 200  */}
                      {isEnglish &&
                        // @ts-ignore
                        contentEn?.length > noticeCharacterLength &&
                        seeMore()}
                      {/* show more button for bangla if content length is > 200  */}
                      {!isEnglish &&
                        // @ts-ignore
                        contentBn?.length > noticeCharacterLengthBn &&
                        seeMore()}
                    </div>
                  ) : (
                    <Translate en={contentEn} bn={contentBn} />
                  )}
                </div>
              </div>
            }
            onClose={handleNoticClose}
            closable
            closeIcon={
              <div className="flex justify-between">
                {/* <div className="text-[60px]" ref={noticeIconRef}>
                  {Icons.megaPhone}
                </div> */}
                <span className="text-[18px] text-pink-500 dark:text-[#EB3E8E]">
                  {Icons.xCircle}
                </span>
              </div>
            }
          />
        </div>
      )}
      {/* notice floating icon */}

      {isMobile ? (
        noticeFloatingButton()
      ) : showTooltip ? (
        <Tooltip
          title={
            // show tooltip message when the isNoticeOpen is true
            // showTooltip ? (
            <Translate
              en="Click to view notice"
              bn="বিজ্ঞপ্তি দেখার জন্যে ক্লিক করুন"
            />
            // ) : (
            //   ''
            // )
          }
        >
          {noticeFloatingButton()}
        </Tooltip>
      ) : (
        noticeFloatingButton()
      )}
    </div>
  );
}

export default Notice;
